import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';

function isPanelFilterSearchFocused(active: Element | null): boolean {
  if (!active) return false;
  return Boolean(active.closest('[data-exploration-filter-panel-search]'));
}

interface UseFilterListKeyboardNavOptions {
  itemCount: number;
  onActivate: (index: number) => void;
  searchInputRef?: RefObject<HTMLInputElement>;
  isDisclosureOpen?: boolean;
  /** When omitted, no element is focused on disclosure open. */
  focusOnOpen?: 'search' | 'firstRow';
  enabled?: boolean;
}

export function useFilterListKeyboardNav({
  itemCount,
  onActivate,
  searchInputRef,
  isDisclosureOpen = false,
  focusOnOpen,
  enabled = true,
}: UseFilterListKeyboardNavOptions) {
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const prevDisclosureOpen = useRef(isDisclosureOpen);

  useEffect(() => {
    rowRefs.current.length = itemCount;
    setFocusedIndex((prev) => {
      if (prev < 0) return prev;
      if (itemCount === 0) return -1;
      return prev >= itemCount ? itemCount - 1 : prev;
    });
  }, [itemCount]);

  const focusRow = useCallback(
    (index: number) => {
      if (index < 0 || index >= itemCount) return;
      setFocusedIndex(index);
      rowRefs.current[index]?.focus();
      rowRefs.current[index]?.scrollIntoView({ block: 'nearest' });
    },
    [itemCount]
  );

  const getRowRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      rowRefs.current[index] = el;
    },
    []
  );

  useEffect(() => {
    const justOpened = isDisclosureOpen && !prevDisclosureOpen.current;
    prevDisclosureOpen.current = isDisclosureOpen;

    if (!enabled || !justOpened || !focusOnOpen) return;
    if (focusOnOpen === 'firstRow' && itemCount === 0) return;
    if (isPanelFilterSearchFocused(document.activeElement)) return;

    if (focusOnOpen === 'search' && searchInputRef?.current) {
      searchInputRef.current.focus();
      return;
    }

    if (itemCount > 0) {
      focusRow(0);
    }
  }, [enabled, focusOnOpen, isDisclosureOpen, itemCount, searchInputRef, focusRow]);

  const handleRowKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLElement>) => {
      if (!enabled) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          focusRow(index < itemCount - 1 ? index + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (index <= 0) {
            searchInputRef?.current?.focus();
            setFocusedIndex(-1);
          } else {
            focusRow(index - 1);
          }
          break;
        case 'Home':
          e.preventDefault();
          focusRow(0);
          break;
        case 'End':
          e.preventDefault();
          focusRow(itemCount - 1);
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          onActivate(index);
          break;
        default:
          break;
      }
    },
    [enabled, itemCount, focusRow, onActivate, searchInputRef]
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown' && itemCount > 0) {
        e.preventDefault();
        focusRow(0);
      }
    },
    [itemCount, focusRow]
  );

  return {
    focusedIndex,
    getRowRef,
    handleRowKeyDown,
    handleSearchKeyDown,
  };
}
